import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import AppSelect from 'src/components/common/AppSelect';
import { Textarea } from 'src/components/ui/textarea';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  CheckCircle,
  CreditCard,
  CalendarIcon,
  Download,
  Loader2,
  ArrowLeft,
  FileText,
  Clock,
  File,
  RefreshCcw,
  IndianRupee,
} from 'lucide-react';
import { StatusPill } from 'src/components/ui/status-pill';
import { useToast } from 'src/hooks/use-toast';
import { useInvoiceInfo } from 'src/services/api/queries/billingQueries';
import {
  useVerifyInvoice,
  useConfirmInvoicePayment,
} from 'src/services/api/mutations/billingMutations';
import { Alert } from 'src/components/common/Alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { format } from 'date-fns';
import { cn } from 'src/lib/utils';
import { PDFDownloadLink } from '@react-pdf/renderer';
import InvoicePDF from 'src/components/InvoicePDF/InvoicePDF';
import { BILLING_STATUS_VALUES } from 'src/utils/statusConstants';

const transformApiResponse = (apiData) => {
  const { header, invoice_details: invoiceDetails, items, meta } = apiData;

  return {
    id: meta.id,
    invoiceNumber: header.invoice_number,
    invoiceDate: header.invoice_date,
    dueDate: header.due_date,
    batchReference: header.batch_reference,
    client: {
      name: header.customer.name || 'N/A',
      id: header.customer.id,
      gstin: header.customer.gstin || 'N/A',
      address: {
        line1: header.customer.address_line_1 || '',
        street: header.customer.street || '',
        zipCode: header.customer.zip_code || '',
      },
    },
    ahedge: {
      name: header.ahedge.name,
      address: header.ahedge.address,
      cityStatePin: header.ahedge.city_state_pin,
      gstin: header.ahedge.gstin,
    },
    invoiceDetails: {
      description: meta.description,
      subTotal: invoiceDetails.sub_total,
      taxAmount: invoiceDetails.tax_amount,
      taxPercentage: invoiceDetails.tax_percentage,
      totalAmount: invoiceDetails.total_amount,
      currency: invoiceDetails.currency,
    },
    items: items.map((item) => ({
      id: item.id,
      candidateName: item.candidate_name,
      candidateId: item.candidate_id,
      requirementRegId: item.registration_id,
      requirementTitle: item.requirement_title,
      requirementId: item.requirement_id,
      poNumber: item.po_number,
      poId: item.po_id,
      description: item.description,
      joiningDate: item.joining_date,
      milestoneType: item.milestone_type,
      installmentOrderNumber: item.installment_order_number,
      milestoneText: item.milestone_text,
      milestoneDate: item.milestone_date,
      ctcAmount: item.ctc_amount,
      costPerCandidate: item.cost_per_candidate,
      amount: item.amount,
    })),
    meta: {
      id: meta.id,
      status: meta.status,
      customerCategory: meta.customer_category,
      milestoneType: meta.milestone_type,
      isProforma: meta.is_proforma,
      createdAt: meta.created_at,
      updatedAt: meta.updated_at,
    },
  };
};

const InfoCard = ({ title, children, className = '', rounded = false }) => (
  <div
    className={`bg-card p-4 ${className} ${rounded ? 'rounded-xl border border-border' : ''}`}
  >
    {title && (
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2">
        {title}
      </h3>
    )}
    {children}
  </div>
);

const StatusTag = ({ status }) => {
  const label = BILLING_STATUS_VALUES[status] || status;
  let textColor = 'slate-600';
  let borderColor = 'slate-200';
  let bgColor = 'slate-50';
  let icon = null;

  switch (status) {
    case 'APPROVED':
      textColor = 'emerald-600';
      borderColor = 'emerald-200';
      bgColor = 'emerald-50';
      icon = <CheckCircle className="w-3.5 h-3.5" />;
      break;
    case 'PAID':
      textColor = 'teal-700';
      borderColor = 'teal-200';
      bgColor = 'teal-50';
      icon = <IndianRupee className="w-3.5 h-3.5" />;
      break;
    case 'PAYMENT_AWAITED':
      textColor = 'amber-700';
      borderColor = 'amber-200';
      bgColor = 'amber-50';
      icon = <Clock className="w-3.5 h-3.5" />;
      break;
    case 'GENERATED':
      textColor = 'blue-700';
      borderColor = 'blue-200';
      bgColor = 'blue-50';
      icon = <File className="w-3.5 h-3.5" />;
      break;
    case 'MODIFIED':
      textColor = 'violet-700';
      borderColor = 'violet-200';
      bgColor = 'violet-50';
      icon = <RefreshCcw className="w-3.5 h-3.5" />;
      break;
    default:
      break;
  }

  return (
    <StatusPill
      name={label}
      textColor={textColor}
      borderColor={borderColor}
      bgColor={bgColor}
      icon={icon}
    />
  );
};

const paymentMethods = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'UPI', label: 'UPI' },
  { value: 'OTHER', label: 'Other' },
];

const InvoiceInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Verify alert state
  const [showVerifyAlert, setShowVerifyAlert] = useState(false);

  // Payment confirmation dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date(),
    payment_reference: '',
    payment_method: '',
    notes: '',
  });

  const {
    data: invoiceResponse,
    isLoading,
    isError,
    error,
  } = useInvoiceInfo(id);
  const verifyInvoice = useVerifyInvoice();
  const confirmPayment = useConfirmInvoicePayment();
  const isApproving = verifyInvoice.isPending;
  const isConfirmingPayment = confirmPayment.isPending;

  // Both mutations invalidate ['invoices', 'info', id], so the query refetches
  // and the status/amounts stay in sync without manual local patching.
  const invoiceData = invoiceResponse?.success
    ? transformApiResponse(invoiceResponse.data)
    : null;

  const handleVerifyInvoice = () => {
    verifyInvoice.mutate(id, {
      onSuccess: (response) => {
        if (response?.success) {
          toast({
            title: 'Success',
            description: 'Invoice has been verified successfully',
          });
          setShowVerifyAlert(false);
        }
      },
      onError: (err) => {
        toast({
          title: 'Error',
          description: err.message || 'Failed to verify invoice',
          variant: 'destructive',
        });
      },
    });
  };

  const handleConfirmPayment = () => {
    // Format the payment data according to API requirements
    const paymentData = {
      payment_date: format(paymentForm.payment_date, 'dd/MM/yyyy'),
      payment_reference: paymentForm.payment_reference,
      payment_method: paymentForm.payment_method,
      notes: paymentForm.notes,
    };

    confirmPayment.mutate(
      { id, data: paymentData },
      {
        onSuccess: (response) => {
          if (response?.success) {
            toast({
              title: 'Success',
              description: 'Payment has been confirmed successfully',
            });
            setShowPaymentDialog(false);
            setPaymentForm({
              payment_date: new Date(),
              payment_reference: '',
              payment_method: '',
              notes: '',
            });
          }
        },
        onError: (err) => {
          toast({
            title: 'Error',
            description: err.message || 'Failed to confirm payment',
            variant: 'destructive',
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin ahBlueText" />
        <p className="mt-4">Loading invoice information...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-500 p-4 border border-red-300 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Error Loading Invoice</h2>
          <p>{error?.message || 'Failed to load invoice information'}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return <div>No invoice data found.</div>;
  }

  const getOrdinalSuffix = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '';

    const tens = number % 100;
    if (tens >= 11 && tens <= 13) return 'th';

    switch (number % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const getMilestoneLabel = (item) => {
    if (item.milestoneType === 'REPLACEMENT') {
      return 'Replacement';
    }

    if (item.milestoneType === 'NORMAL') {
      const order = Number(item.installmentOrderNumber);
      if (!Number.isNaN(order) && order > 0) {
        const suffix = getOrdinalSuffix(order);
        return `${order}${suffix} installment`;
      }
      return 'Installment';
    }

    return item.installmentOrderNumber || item.milestoneText || '-';
  };

  const currencySymbol =
    invoiceData.invoiceDetails.currency === 'INR' ? '₹' : '$';

  return (
    <div className="text-left space-y-4 bg-muted">
      <PageHeader
        title={`Invoice # ${invoiceData.invoiceNumber}`}
        actions={
          <>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => navigate('/invoices')}
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              Back to Invoices
            </Button>
            {(invoiceData.meta.status === 'SENT' ||
              invoiceData.meta.status === 'PAID' ||
              invoiceData.meta.status === 'OVERDUE') && (
              <PDFDownloadLink
                document={<InvoicePDF data={invoiceData} />}
                fileName={`Invoice_${invoiceData.invoiceNumber}.pdf`}
                className="no-underline"
              >
                {({ loading }) => (
                  <Button
                    disabled={loading}
                    variant="outline"
                    size="headerSm"
                    className="gap-1.5"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        Download PDF
                        <Download className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                )}
              </PDFDownloadLink>
            )}
          </>
        }
      />

      <div className="p-4 px-40 flex gap-4 flex-col">
        <div className="shadow-[0_1px_8px_rgba(0,0,0,.08)] rounded-xl border border-border p-6 bg-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-md text-primary flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Invoice
            </h2>
            {invoiceData.meta.status === 'GENERATED' && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-emerald-500 text-white hover:bg-emerald-600"
                onClick={() => setShowVerifyAlert(true)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Verify
              </Button>
            )}
            {invoiceData.meta.status === 'PAYMENT_AWAITED' && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-primary text-white hover:bg-brand-deep"
                onClick={() => setShowPaymentDialog(true)}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Confirm Payment
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-border pb-6">
            <InfoCard title="From" className="pl-0">
              <h4 className="text-sm font-semibold text-base">
                {invoiceData.ahedge.name}
              </h4>
              <p className="text-xs text-gray-600 mt-1">
                {invoiceData.ahedge.address}
              </p>
              <p className="text-xs text-gray-600">
                {invoiceData.ahedge.cityStatePin}
              </p>
              <p className="text-xs mt-2 flex gap-2 items-center">
                <span className="text-muted-foreground font-normal text-[11px]">
                  GSTIN:
                </span>{' '}
                <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                  {invoiceData.ahedge.gstin}
                </span>
              </p>
            </InfoCard>

            <InfoCard title="To">
              <h4 className="text-sm font-semibold text-base">
                {invoiceData.client.name}
              </h4>
              {invoiceData.client.address && (
                <>
                  {invoiceData.client.address.line1 && (
                    <p className="text-xs text-gray-600 mt-1">
                      {invoiceData.client.address.line1}
                    </p>
                  )}
                  {(invoiceData.client.address.street ||
                    invoiceData.client.address.zipCode) && (
                    <p className="text-xs text-gray-600">
                      {[
                        invoiceData.client.address.street,
                        invoiceData.client.address.zipCode,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </>
              )}
              {invoiceData.client.gstin &&
                invoiceData.client.gstin !== 'N/A' && (
                  <p className="text-xs mt-2 flex gap-2 items-center">
                    <span className="text-muted-foreground font-normal text-[11px]">
                      GSTIN:
                    </span>{' '}
                    <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                      {invoiceData.client.gstin}
                    </span>
                  </p>
                )}
            </InfoCard>
            <InfoCard rounded className="!bg-[#F8FAFC] grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1 justify-between">
                <span className="text-xs text-gray-700">Invoice Date</span>
                <span className="text-xs font-bold">
                  {invoiceData.invoiceDate}
                </span>
              </div>
              <div className="flex flex-col gap-1 justify-between">
                <span className="text-xs text-gray-700">Due Date</span>
                <span className="text-xs font-bold">{invoiceData.dueDate}</span>
              </div>
              <div className="flex flex-col gap-1 justify-between">
                <span className="text-xs text-gray-700">Status</span>
                <span className="text-xs font-bold w-fit">
                  <StatusTag status={invoiceData.meta.status} />
                </span>
              </div>
            </InfoCard>
          </div>

          <InfoCard title="SUMMARY" className="mb-6 px-0">
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-border">
                    <tr className="bg-card text-muted-foreground">
                      <th className="p-3 py-2 text-left font-normal">
                        DESCRIPTION
                      </th>
                      <th className="p-3 py-2 text-right font-normal">NOS</th>
                      <th className="p-3 py-2 text-right font-normal">TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      key="item-summary"
                      className="border-b border-slate-100"
                    >
                      <td className="p-3 font-medium">
                        {invoiceData.invoiceDetails?.description}
                      </td>
                      <td className="p-3 text-right">
                        {invoiceData.items.length}
                      </td>
                      <td className="p-3 text-right">
                        {currencySymbol}
                        {invoiceData.invoiceDetails?.subTotal?.toLocaleString(
                          'en-IN',
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end p-4 pt-3">
              <div className="w-full max-w-xs text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">
                    GST ({invoiceData.invoiceDetails.taxPercentage}%)
                  </span>
                  <span className="text-slate-700 font-medium">
                    {currencySymbol}
                    {invoiceData.invoiceDetails.taxAmount?.toLocaleString(
                      'en-IN',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </span>
                </div>
                <div className="border-t border-border my-2" />
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground-strong">
                    Grand Total
                  </span>
                  <span
                    className="text-[15px] font-bold text-primary"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {currencySymbol}
                    {invoiceData.invoiceDetails.totalAmount?.toLocaleString(
                      'en-IN',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </span>
                </div>
              </div>
            </div>
          </InfoCard>

          <div className="mt-8 space-y-6">
            <div className="bg-card border border-border rounded-lg p-2 text-center">
              <p className="text-xs text-slate-500">
                This is a system-generated invoice. No signature required.
              </p>
            </div>

            <div className="flex flex-col gap-4 justify-between items-start pt-4">
              <div className="flex-1">
                <h4 className="font-bold text-xs mb-2">Terms</h4>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>
                    • Payment via NEFT/RTGS within 30 days of invoice receipt
                    unless otherwise agreed.
                  </li>
                  <li>• For queries, contact billing@ahedge.in</li>
                </ul>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Authorised by
                  <span className="text-xs font-medium text-foreground-strong mb-2 ml-1">
                    Ahedge Finance
                  </span>
                </div>
                <div className="text-xs text-slate-500 italic">
                  Digitally approved — no signature required
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="shadow-[0_1px_8px_rgba(0,0,0,.08)] rounded-xl border border-border p-6 bg-card">
          <InfoCard title="INVOICE ITEMS" className="mb-6 px-0">
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-border">
                    <tr className="bg-card text-muted-foreground">
                      <th className="p-3 py-2 text-left font-normal">
                        CANDIDATE
                      </th>
                      <th className="p-3 py-2 text-left font-normal">
                        REQUIREMENT
                      </th>
                      <th className="p-3 py-2 text-left font-normal">
                        PO NUMBER
                      </th>
                      <th className="p-3 py-2 text-left font-normal">
                        MILESTONE
                      </th>
                      <th className="p-3 py-2 text-right font-normal">CTC</th>
                      <th className="p-3 py-2 text-right font-normal">
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, index) => (
                      <tr
                        key={`item-${item.id}`}
                        className={`${
                          index < invoiceData.items.length - 1
                            ? 'border-b border-slate-100'
                            : ''
                        }`}
                      >
                        <td className="p-3 font-medium">
                          {item.candidateName}
                        </td>
                        <td className="p-3">{item.requirementRegId}</td>
                        <td className="p-3">{item.poNumber}</td>
                        <td className="p-3">
                          {item.milestoneText}
                          <div className="text-xs">
                            {getMilestoneLabel(item)}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          {currencySymbol}
                          {item.ctcAmount?.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-3 text-right font-medium">
                          {currencySymbol}
                          {item.amount?.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </InfoCard>
        </div>
      </div>

      {/* Verify Invoice Alert */}
      <Alert
        isOpen={showVerifyAlert}
        onClose={() => setShowVerifyAlert(false)}
        title="Verify Invoice"
        description={`Are you sure you want to verify invoice ${invoiceData.invoiceNumber}?`}
        cancelText="Cancel"
        continueText={isApproving ? 'Processing...' : 'Verify'}
        onCancel={() => setShowVerifyAlert(false)}
        onContinue={handleVerifyInvoice}
      />

      {/* Payment Form Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>
              Enter payment details for invoice {invoiceData.invoiceNumber}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Amount Paid */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="amount_paid" className="text-right">
                Amount
              </Label>
              <div className="col-span-3">
                {currencySymbol}
                {Number(
                  invoiceData.invoiceDetails.totalAmount || 0
                ).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>

            {/* Payment Date */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="payment_date" className="text-right">
                Date
              </Label>
              <div className="col-span-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !paymentForm.payment_date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {paymentForm.payment_date ? (
                        format(paymentForm.payment_date, 'PPP')
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={paymentForm.payment_date}
                      onSelect={(date) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          payment_date: date,
                        }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Payment Reference */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="payment_reference" className="text-right">
                Reference
              </Label>
              <div className="col-span-3">
                <Input
                  id="payment_reference"
                  value={paymentForm.payment_reference}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      payment_reference: e.target.value,
                    }))
                  }
                  placeholder="Payment reference number"
                  className="w-full"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="payment_method" className="text-right">
                Method
              </Label>
              <div className="col-span-3">
                <AppSelect
                  options={paymentMethods}
                  value={paymentForm.payment_method}
                  onValueChange={(value) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      payment_method: value,
                    }))
                  }
                  placeholder="Select payment method"
                  className="w-full"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="notes" className="text-right">
                Notes
              </Label>
              <div className="col-span-3">
                <Textarea
                  id="notes"
                  value={paymentForm.notes}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Additional notes"
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPaymentDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPayment}
              disabled={isConfirmingPayment}
            >
              {isConfirmingPayment ? 'Processing...' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceInfo;
